import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PENDING_INQUIRY_DRAFT_KEY = 'primum_pending_inquiry_draft';

export interface PendingInquiryDraft {
  cancerFocusId: string;
  message: string;
  noPII: boolean;
  anonymous: boolean;
}

/**
 * Saves pending inquiry draft data to local storage.
 * Used when a logged-out user fills out the inquiry form and triggers auth.
 * The data is later retrieved by /inquiry/redirect to create a server draft.
 */
export async function savePendingInquiryDraft(draft: PendingInquiryDraft): Promise<void> {
  try {
    const data = JSON.stringify(draft);

    if (isWeb && typeof window !== 'undefined') {
      window.localStorage.setItem(PENDING_INQUIRY_DRAFT_KEY, data);
    } else {
      await AsyncStorage.setItem(PENDING_INQUIRY_DRAFT_KEY, data);
    }
  } catch (error) {
    console.error('Failed to save pending inquiry draft:', error);
  }
}

/**
 * Loads pending inquiry draft data from local storage.
 * Returns null if no draft exists or if parsing fails.
 */
export async function loadPendingInquiryDraft(): Promise<PendingInquiryDraft | null> {
  try {
    let data: string | null = null;

    if (isWeb && typeof window !== 'undefined') {
      data = window.localStorage.getItem(PENDING_INQUIRY_DRAFT_KEY);
    } else {
      data = await AsyncStorage.getItem(PENDING_INQUIRY_DRAFT_KEY);
    }

    if (!data) {
      return null;
    }

    return JSON.parse(data) as PendingInquiryDraft;
  } catch (error) {
    console.error('Failed to load pending inquiry draft:', error);

    return null;
  }
}

/**
 * Clears pending inquiry draft data from local storage.
 * Called after successfully creating a server draft.
 */
export async function clearPendingInquiryDraft(): Promise<void> {
  try {
    if (isWeb && typeof window !== 'undefined') {
      window.localStorage.removeItem(PENDING_INQUIRY_DRAFT_KEY);
    } else {
      await AsyncStorage.removeItem(PENDING_INQUIRY_DRAFT_KEY);
    }
  } catch (error) {
    console.error('Failed to clear pending inquiry draft:', error);
  }
}

/**
 * Checks if a pending inquiry draft exists in local storage.
 */
export async function hasPendingInquiryDraft(): Promise<boolean> {
  try {
    let data: string | null = null;

    if (isWeb && typeof window !== 'undefined') {
      data = window.localStorage.getItem(PENDING_INQUIRY_DRAFT_KEY);
    } else {
      data = await AsyncStorage.getItem(PENDING_INQUIRY_DRAFT_KEY);
    }

    return data !== null;
  } catch (error) {
    return false;
  }
}
